import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import type { PublicPage, PublicScreenProps } from "app/router/public/types";
export {
  buildDebtSecurityDetailPath,
  buildFundDetailPath,
} from "modules/public-investor-area/model/investorAreaPaths";

export interface PublicRouteDefinition {
  path: string;
  title: string;
  description: string;
  component: LazyExoticComponent<ComponentType<PublicScreenProps>>;
  overlayHeader?: boolean;
}

export const disabledPublicPages = new Set<PublicPage>([
  "fundos",
  "detalhes-fundo",
  "eventos",
]);

export function isPublicPageDisabled(page: PublicPage) {
  return disabledPublicPages.has(page);
}

export const publicRoutes = {
  home: {
    path: "/",
    title: "OXY DTVM | Infraestrutura para o mercado de capitais",
    description:
      "Portal institucional da OXY DTVM com soluções, governança, suporte, informações regulatórias e canais públicos obrigatórios.",
    component: lazy(() =>
      import("pages/public/home").then((module) => ({
        default: module.HomePage,
      })),
    ),
    overlayHeader: true,
  },
  "solucao-registro-custodia": {
    path: "/solucoes/registro-custodia",
    title: "Registro e Custódia para Estruturas do Mercado de Capitais | OXY DTVM",
    description:
      "Conheça a estrutura da OXY DTVM para registro, custódia, controle operacional e acompanhamento de operações vinculadas ao mercado de capitais.",
    component: lazy(() =>
      import("pages/public/institutional/real-estate-registration-custody").then((module) => ({
        default: module.RealEstateRegistrationCustodyPage,
      })),
    ),
    overlayHeader: true,
  },
  "transferencia-valores-mobiliarios": {
    path: "/solucoes/registro-custodia/transferencia-valores-mobiliarios",
    title: "Solicitação de Transferência de Valores Mobiliários | OXY DTVM",
    description:
      "Consulte requisitos, documentação, fluxo operacional e controles aplicáveis à solicitação de transferência de valores mobiliários na OXY DTVM.",
    component: lazy(() =>
      import("pages/public/institutional/securities-transfer").then((module) => ({
        default: module.SecuritiesTransferPage,
      })),
    ),
    overlayHeader: true,
  },
  "solucao-escrituracao": {
    path: "/solucoes/escrituracao",
    title: "Escrituração para Emissões e Estruturas do Mercado de Capitais | OXY DTVM",
    description:
      "Entenda como a OXY DTVM conduz escrituração, eventos, posicionamento e comunicação para operações vinculadas ao mercado de capitais.",
    component: lazy(() =>
      import("pages/public/institutional/real-estate-book-entry").then((module) => ({
        default: module.RealEstateBookEntryPage,
      })),
    ),
    overlayHeader: true,
  },
  "solucao-titulos-divida": {
    path: "/solucoes/titulos-divida",
    title: "Títulos de Dívida e Emissões Estruturadas | OXY DTVM",
    description:
      "Página institucional sobre estruturação, coordenação operacional e acompanhamento de títulos de dívida e emissões estruturadas pela OXY DTVM.",
    component: lazy(() =>
      import("pages/public/institutional/debt-structuring").then((module) => ({
        default: module.DebtStructuringPage,
      })),
    ),
    overlayHeader: true,
  },
  fundos: {
    path: "/fundos-investimento",
    title: "Fundos de Investimento | OXY DTVM",
    description:
      "Consulte informações sobre fundos de investimento na Área do Investidor da OXY DTVM.",
    component: lazy(() =>
      import("pages/public/investor-area/funds").then((module) => ({
        default: module.FundsPage,
      })),
    ),
  },
  "detalhes-fundo": {
    path: "/fundos-investimento/:id",
    title: "Detalhes do Fundo | OXY DTVM",
    description:
      "Consulte informações, documentos e histórico de cotas de fundos de investimento administrados pela OXY DTVM.",
    component: lazy(() =>
      import("pages/public/investor-area/fund-detail").then((module) => ({
        default: module.FundDetailPage,
      })),
    ),
  },
  eventos: {
    path: "/assembleias-eventos",
    title: "Assembleias e Eventos | OXY DTVM",
    description:
      "Acompanhe assembleias, eventos societários e informações públicas relevantes da OXY DTVM.",
    component: lazy(() =>
      import("pages/public/investor-area/events").then((module) => ({
        default: module.InvestorEventsPage,
      })),
    ),
  },
  titulos: {
    path: "/titulos-divida",
    title: "Títulos de Dívida | OXY DTVM",
    description:
      "Consulte informações públicas sobre títulos de dívida e emissões estruturadas pela OXY DTVM.",
    component: lazy(() =>
      import("pages/public/investor-area/debt-securities").then((module) => ({
        default: module.DebtSecuritiesPage,
      })),
    ),
  },
  "detalhes-titulo": {
    path: "/titulos-divida/:id",
    title: "Detalhes da Emissão | OXY DTVM",
    description:
      "Consulte informações da emissão, histórico de PU, documentos, relatórios, assembleias e fatos relevantes.",
    component: lazy(() =>
      import("pages/public/investor-area/debt-security-detail").then((module) => ({
        default: module.DebtSecurityDetailPage,
      })),
    ),
  },
  "quem-somos": {
    path: "/quem-somos",
    title: "Quem Somos | OXY DTVM",
    description:
      "Conheça a história, diferenciais, governança e estrutura institucional da OXY DTVM.",
    component: lazy(() =>
      import("pages/public/institutional/about").then((module) => ({
        default: module.AboutPage,
      })),
    ),
    overlayHeader: true,
  },
  "fale-conosco": {
    path: "/fale-conosco",
    title: "Fale Conosco | OXY DTVM",
    description:
      "Entre em contato com a OXY DTVM para atendimento comercial, temas institucionais e demandas operacionais.",
    component: lazy(() =>
      import("pages/public/institutional/contact").then((module) => ({
        default: module.ContactPage,
      })),
    ),
    overlayHeader: true,
  },
  "centro-ajuda": {
    path: "/centro-ajuda",
    title: "Centro de Ajuda | OXY DTVM",
    description:
      "FAQ, materiais explicativos, tutoriais e canais de suporte da OXY DTVM em uma única área.",
    component: lazy(() =>
      import("pages/public/institutional/help-center").then((module) => ({
        default: module.HelpCenterPage,
      })),
    ),
    overlayHeader: true,
  },
  "canal-denuncias": {
    path: "/canal-denuncias",
    title: "Canal de Denúncias | OXY DTVM",
    description:
      "Canal independente para registro de denúncias com anonimato, anexação de documentos e fluxo para compliance.",
    component: lazy(() =>
      import("pages/public/institutional/ethics-reporting").then((module) => ({
        default: module.EthicsReportingPage,
      })),
    ),
    overlayHeader: true,
  },
  ouvidoria: {
    path: "/ouvidoria",
    title: "Ouvidoria | OXY DTVM",
    description:
      "Página pública da Ouvidoria com formulário de reclamações, consulta de protocolo, contatos, prazo de resposta e informações regulatórias.",
    component: lazy(() =>
      import("pages/public/institutional/ouvidoria").then((module) => ({
        default: module.OmbudsmanPage,
      })),
    ),
    overlayHeader: true,
  },
  "informacoes-regulatorias": {
    path: "/informacoes-regulatorias",
    title: "Informações Regulatórias | OXY DTVM",
    description:
      "Biblioteca pública com políticas institucionais de integridade, PLD/FTP, relacionamento com clientes, segurança da informação e proteção de dados.",
    component: lazy(() =>
      import("pages/public/institutional/governance").then((module) => ({
        default: module.GovernancePage,
      })),
    ),
    overlayHeader: true,
  },
  transparencia: {
    path: "/transparencia",
    title: "Transparência | OXY DTVM",
    description:
      "Demonstrações financeiras da OXY DTVM publicadas por exercício, revisadas por auditoria independente e disponíveis para consulta e download em PDF.",
    component: lazy(() =>
      import("pages/public/institutional/transparency").then((module) => ({
        default: module.TransparencyPage,
      })),
    ),
    overlayHeader: true,
  },
  "privacidade-lgpd": {
    path: "/politica-privacidade-lgpd",
    title: "Política de Privacidade e Proteção de Dados | OXY DTVM",
    description:
      "Conheça finalidades do tratamento, bases legais, direitos do titular, cookies e contato do DPO da OXY DTVM.",
    component: lazy(() =>
      import("pages/public/institutional/privacy").then((module) => ({
        default: module.PrivacyPage,
      })),
    ),
    overlayHeader: true,
  },
} satisfies Record<PublicPage, PublicRouteDefinition>;

const publicRoutePathnameMap = Object.values(publicRoutes).reduce(
  (accumulator, definition) => {
    accumulator[definition.path] = definition;
    return accumulator;
  },
  {} as Record<string, PublicRouteDefinition>,
);

export function findRouteByPathname(pathname: string) {
  return publicRoutePathnameMap[pathname];
}
